import {take} from "rxjs/operators";
import {AppsGroupsRulesPreview, NetscopeApiService, Success} from "@app/services/netscope-api.service";
import {AppsGroups} from "@app/netscope/model/data-models";
import {AppsGroupsInventory} from "@app/netscope/netscope-common-flowview/misc/AppsGroupsInventory";

// Why a resource belongs to an apps group: picked by hand, matched by the name rules, or brought by an
// apps group the group includes
export type MemberSource = "picked" | "rule" | "included";

export class ResultingMember {
  uuid: string;
  name: string;
  type: string;
  source: MemberSource;
  // Apps group the member was brought by, for the ones coming from an included group
  via?: string;
}

// An apps group of any file, as the inclusions point to it
export class AppsGroupRef {
  name: string;
  file: string;
}

export type PatternKind = "include" | "exclude";

/**
 * What the name rules of an apps group match in the latest inventory, as the appliance last answered. The
 * appliance only sends the first members, 'total' counts all of them.
 */
export class RulesPreview {
  loading: boolean = false;
  failed: boolean = false;
  total?: number;
  members?: { uuid: string, name: string, type: string }[];
}

// Names the appliance accepts for an apps group
const VALID_NAME = /^[A-Za-z0-9_]+$/;
// Limits of the name rules, which the appliance checks as well
export const MAX_PATTERNS_COUNT = 50;
export const MAX_PATTERN_LENGTH = 200;
const PREVIEW_DELAY_MS = 300;

/**
 * The apps groups of one file as the user edits them, next to the groups of the other files, which they can
 * include and which are never edited here.
 *
 * The groups are kept as the file holds them: 'vms' only lists the picked members, the members of the included
 * groups are gathered when needed (see members), and the ones matched by the rules are in 'matched_vms', which
 * the appliance fills when the file is saved and the previews fill as the rules are typed.
 */
export class AppsGroupsEditing {

  groups: AppsGroups[] = [];
  // The file as last loaded or saved, to tell whether it changed and to discard the changes
  private savedContent: string = "[]";
  dirty = false;

  // Members of the groups, computed once until something changes: the page reads them many times per check
  private membersCache: Map<AppsGroups, ResultingMember[]> = new Map();
  // Grows each time the members may have changed, for the views keeping what they derive from them
  revision = 0;

  rulesPreviews: Map<AppsGroups, RulesPreview> = new Map();
  // Number of resources each pattern matches on its own, shown next to it
  patternHits: Map<string, number> = new Map();
  private previewTimers: Map<AppsGroups, any> = new Map();
  private previewRequestIds: Map<AppsGroups, number> = new Map();

  constructor(public file: string,
              public readOnly: boolean,
              groups: AppsGroups[],
              // Apps groups of the other files, indexed by file
              public otherFiles: Map<string, AppsGroups[]>,
              public inventory: AppsGroupsInventory,
              private netscopeService: NetscopeApiService,
              // Called when something the user sees changed on its own, a preview coming back for instance
              private onChange: () => void = () => {}) {
    this.groups = groups.map((group) => AppsGroupsEditing.cleanCopy(group));
    this.savedContent = JSON.stringify(this.groups);
    for (let group of this.groups) {
      this.loadPatternHits(group);
    }
  }

  // A group as the file holds it: the fields added while drawing it are left behind
  static cleanCopy(group: AppsGroups): AppsGroups {
    let copy: any = {
      name: group.name,
      vms: (group.vms ?? []).map((vm) => ({uuid: vm.uuid})),
      metadata: {...(group.metadata ?? {})},
    };
    if (group.description) {
      copy.description = group.description;
    }
    if ((group.included_apps_groups ?? []).length > 0) {
      copy.included_apps_groups = group.included_apps_groups
        .map((included) => included.file == undefined ? {name: included.name} : {name: included.name, file: included.file});
    }
    if ((group.members_rules?.include ?? []).length > 0 || (group.members_rules?.exclude ?? []).length > 0) {
      copy.members_rules = {include: [...(group.members_rules.include ?? [])], exclude: [...(group.members_rules.exclude ?? [])]};
    }
    if ((group.matched_vms ?? []).length > 0) {
      copy.matched_vms = group.matched_vms.map((vm) => ({uuid: vm.uuid}));
    }
    return copy as AppsGroups;
  }

  // ---------------------------------------------------------------- Changes

  changed = () => {
    this.dirty = JSON.stringify(this.groups) != this.savedContent;
    this.membersCache.clear();
    this.revision++;
  }

  // Something the members depend on changed without the user editing anything, a preview coming back
  private refreshed = () => {
    this.membersCache.clear();
    this.revision++;
    this.onChange();
  }

  discard = () => {
    this.groups = (JSON.parse(this.savedContent) as AppsGroups[]);
    this.rulesPreviews = new Map();
    this.dirty = false;
    this.membersCache.clear();
    this.revision++;
  }

  /**
   * What is written back to the file: the members matched by the rules are not sent, the appliance computes
   * them again as it saves.
   */
  payload = () => {
    return {
      apps_groups: this.groups.map((group) => {
        let copy: any = AppsGroupsEditing.cleanCopy(group);
        delete copy.matched_vms;
        return copy;
      })
    };
  }

  // ---------------------------------------------------------------- Groups

  groupsOf = (file: string): AppsGroups[] => {
    return file == this.file ? this.groups : (this.otherFiles.get(file) ?? []);
  }

  find = (ref: AppsGroupRef): AppsGroups => {
    return this.groupsOf(ref.file).find((group) => group.name == ref.name);
  }

  // Apps group an inclusion points to: one naming no file points inside the file holding the including group
  refOf = (included: { name: string, file?: string }, includingFile: string): AppsGroupRef => {
    return {name: included.name, file: included.file ?? includingFile};
  }

  nameError = (group: AppsGroups, name: string = group.name): string => {
    if (!VALID_NAME.test(name ?? "")) {
      return "Only letters, digits and '_', without spaces.";
    }
    if (this.groups.some((other) => other !== group && other.name == name)) {
      return "Another group of this file has this name.";
    }
    return undefined;
  }

  // A name no group of the file has yet
  freeName = (base: string): string => {
    base = (base ?? "").replace(/[^A-Za-z0-9_]+/g, "_").replace(/^_+|_+$/g, "") || "new_group";
    let name = base;
    let index = 2;
    while (this.groups.some((group) => group.name == name)) {
      name = `${base}_${index}`;
      index += 1;
    }
    return name;
  }

  createGroup = (name: string): AppsGroups => {
    let group = {name: name, vms: [], metadata: {}} as AppsGroups;
    this.groups.push(group);
    this.changed();
    return group;
  }

  /**
   * Renames a group. The groups of this file including it point to it by name, and are made to follow it.
   * The groups of the other files are not saved from here: theirs would point to a group that is not there
   * anymore.
   */
  rename = (group: AppsGroups, name: string) => {
    let previousName = group.name;
    group.name = name;

    for (let other of this.groups) {
      for (let included of other.included_apps_groups ?? []) {
        if (included.file == undefined && included.name == previousName) {
          included.name = name;
        }
      }
    }
    this.changed();
  }

  setDescription = (group: AppsGroups, description: string) => {
    if ((description ?? "").trim() === "") {
      delete group.description;
    } else {
      group.description = description;
    }
    this.changed();
  }

  deleteGroup = (group: AppsGroups) => {
    this.groups = this.groups.filter((other) => other !== group);
    for (let other of this.groups) {
      if (other.included_apps_groups != undefined) {
        other.included_apps_groups = other.included_apps_groups
          .filter((included) => !(included.file == undefined && included.name == group.name));
      }
    }
    this.changed();
  }

  // Groups of other files that include one of this file, which would lose it if it were renamed or deleted
  externalParentsCount = (group: AppsGroups): number => {
    return this.parentsOf(group).filter((parent) => parent.file != this.file).length;
  }

  // ---------------------------------------------------------------- Members

  /**
   * Members of a group and why each one is there. A member is only listed once, for the first reason found:
   * picked first, then matched by the rules, then brought by the included groups in their order. A group
   * already walked is not walked twice, so that groups including each other cannot loop.
   */
  members = (group: AppsGroups, file: string = this.file, visited: Set<string> = new Set()): ResultingMember[] => {
    if (group == undefined || visited.has(`${file}/${group.name}`)) {
      return [];
    }
    let cacheable = visited.size == 0;
    if (cacheable && this.membersCache.has(group)) {
      return this.membersCache.get(group);
    }
    visited.add(`${file}/${group.name}`);

    let byUuid: Map<string, ResultingMember> = new Map();
    let add = (uuid: string, source: MemberSource, via?: string) => {
      if (!byUuid.has(uuid)) {
        byUuid.set(uuid, {
          uuid: uuid,
          name: this.inventory.resourceLabel(uuid),
          type: this.inventory.memberType(uuid),
          source: source,
          via: via,
        });
      }
    };

    for (let vm of group.vms ?? []) {
      add(vm.uuid, "picked");
    }
    for (let vm of group.matched_vms ?? []) {
      add(vm.uuid, "rule");
    }
    for (let included of group.included_apps_groups ?? []) {
      let ref = this.refOf(included, file);
      for (let member of this.members(this.find(ref), ref.file, new Set(visited))) {
        add(member.uuid, "included", included.name);
      }
    }

    let members = Array.from(byUuid.values());
    if (cacheable) {
      this.membersCache.set(group, members);
    }
    return members;
  }

  // ---------------------------------------------------------------- Picked resources

  addPicked = (group: AppsGroups, uuid: string) => {
    group.vms = group.vms ?? [];
    if (!group.vms.some((vm) => vm.uuid == uuid)) {
      group.vms.push({uuid: uuid});
      this.changed();
    }
  }

  removePicked = (group: AppsGroups, uuid: string) => {
    group.vms = (group.vms ?? []).filter((vm) => vm.uuid != uuid);
    this.changed();
  }

  // ---------------------------------------------------------------- Name rules

  patterns = (group: AppsGroups, kind: PatternKind): string[] => {
    return group.members_rules?.[kind] ?? [];
  }

  patternsError = (group: AppsGroups): string => {
    for (let kind of ["include", "exclude"] as PatternKind[]) {
      let patterns = this.patterns(group, kind);
      if (patterns.length > MAX_PATTERNS_COUNT) {
        return `At most ${MAX_PATTERNS_COUNT} patterns are allowed.`;
      }
      if (patterns.some((pattern) => pattern.length > MAX_PATTERN_LENGTH)) {
        return `The patterns are limited to ${MAX_PATTERN_LENGTH} characters.`;
      }
    }
    return undefined;
  }

  addPattern = (group: AppsGroups, kind: PatternKind, pattern: string) => {
    pattern = (pattern ?? "").trim();
    if (pattern === "" || this.patterns(group, kind).includes(pattern)) {
      return;
    }
    group.members_rules = {
      include: [...this.patterns(group, "include")],
      exclude: [...this.patterns(group, "exclude")],
    };
    group.members_rules[kind].push(pattern);
    this.rulesChanged(group);
  }

  removePattern = (group: AppsGroups, kind: PatternKind, pattern: string) => {
    let include = this.patterns(group, "include").filter((other) => kind != "include" || other != pattern);
    let exclude = this.patterns(group, "exclude").filter((other) => kind != "exclude" || other != pattern);
    if (include.length == 0 && exclude.length == 0) {
      delete group.members_rules;
    } else {
      group.members_rules = {include: include, exclude: exclude};
    }
    this.rulesChanged(group);
  }

  /**
   * The rules of a group changed: what they match is asked to the appliance once the user pauses. The members
   * follow the rules right away when the appliance sent all of them, otherwise once the file is saved.
   */
  private rulesChanged = (group: AppsGroups) => {
    this.changed();
    this.loadPatternHits(group);

    clearTimeout(this.previewTimers.get(group));
    if (this.patterns(group, "include").length == 0 || this.patternsError(group) != undefined) {
      this.rulesPreviews.delete(group);
      if (this.patterns(group, "include").length == 0) {
        delete group.matched_vms;
      }
      return;
    }

    let preview = this.rulesPreviews.get(group) ?? new RulesPreview();
    preview.loading = true;
    this.rulesPreviews.set(group, preview);
    this.previewTimers.set(group, setTimeout(() => this.previewRules(group), PREVIEW_DELAY_MS));
  }

  private previewRules = async (group: AppsGroups) => {
    // Only the answer to the last rules typed is kept, an earlier one may come back after it
    let requestId = (this.previewRequestIds.get(group) ?? 0) + 1;
    this.previewRequestIds.set(group, requestId);

    let preview = await this.preview(this.patterns(group, "include"), this.patterns(group, "exclude"));
    if (this.previewRequestIds.get(group) != requestId) {
      return;
    }

    this.rulesPreviews.set(group, preview);
    if (!preview.failed && preview.members.length == preview.total) {
      group.matched_vms = preview.members.map((member) => ({uuid: member.uuid}));
    }
    this.refreshed();
  }

  // Asks the appliance what rules match in the latest inventory
  preview = async (include: string[], exclude: string[]): Promise<RulesPreview> => {
    let response = await this.netscopeService.previewAppsGroupsRules(include, exclude).pipe(take(1)).toPromise();
    if (!(response instanceof Success)) {
      return {loading: false, failed: true};
    }
    let element: AppsGroupsRulesPreview = response.element;
    return {loading: false, failed: false, total: element.total, members: element.members};
  }

  // Resources matched by the rules, the last preview first, then the members the appliance computed on save
  ruleMatchesCount = (group: AppsGroups): number => {
    let preview = this.rulesPreviews.get(group);
    if (preview?.loading) {
      return undefined;
    }
    if (preview?.total != undefined) {
      return preview.total;
    }
    return (group.matched_vms ?? []).length;
  }

  // Asks how many resources each pattern of the group matches on its own, the ones already known aside
  private loadPatternHits = (group: AppsGroups) => {
    let patterns = this.patterns(group, "include").concat(this.patterns(group, "exclude"));
    for (let pattern of patterns) {
      if (this.patternHits.has(pattern)) {
        continue;
      }
      this.patternHits.set(pattern, undefined);
      this.preview([pattern], []).then((preview) => {
        if (!preview.failed) {
          this.patternHits.set(pattern, preview.total);
          this.refreshed();
        }
      });
    }
  }

  // ---------------------------------------------------------------- Included groups

  /**
   * Whether a group includes another one, directly or through the groups it includes itself. A group already
   * walked is not walked twice, a hand-written file may hold a cycle.
   */
  includes = (group: AppsGroups, file: string, target: AppsGroupRef, visited: Set<string> = new Set()): boolean => {
    if (group == undefined || visited.has(`${file}/${group.name}`)) {
      return false;
    }
    visited.add(`${file}/${group.name}`);

    for (let included of group.included_apps_groups ?? []) {
      let ref = this.refOf(included, file);
      if (ref.name == target.name && ref.file == target.file) {
        return true;
      }
      if (this.includes(this.find(ref), ref.file, target, visited)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Groups the given one may include, from every file, this one first: itself and the ones it includes
   * already are left out. The ones including it, at any depth, are kept but blocked, as the two would then
   * contain each other.
   */
  includeCandidates = (group: AppsGroups): { ref: AppsGroupRef, blocked: boolean }[] => {
    let target: AppsGroupRef = {name: group.name, file: this.file};
    let files = [this.file].concat(Array.from(this.otherFiles.keys()).filter((file) => file != this.file).sort());
    let candidates = [];

    for (let file of files) {
      for (let candidate of this.groupsOf(file)) {
        if (candidate === group) {
          continue;
        }
        let alreadyIncluded = (group.included_apps_groups ?? [])
          .some((included) => included.name == candidate.name && (included.file ?? this.file) == file);
        if (alreadyIncluded) {
          continue;
        }
        candidates.push({ref: {name: candidate.name, file: file}, blocked: this.includes(candidate, file, target)});
      }
    }
    return candidates;
  }

  include = (group: AppsGroups, ref: AppsGroupRef) => {
    group.included_apps_groups = group.included_apps_groups ?? [];
    // An inclusion inside the same file names no file, so that the file can be copied without its groups
    // pointing back at it
    group.included_apps_groups.push(ref.file == this.file ? {name: ref.name} : {name: ref.name, file: ref.file});
    this.changed();
  }

  removeInclusion = (group: AppsGroups, ref: AppsGroupRef) => {
    group.included_apps_groups = (group.included_apps_groups ?? [])
      .filter((included) => !(included.name == ref.name && (included.file ?? this.file) == ref.file));
    if (group.included_apps_groups.length == 0) {
      delete group.included_apps_groups;
    }
    this.changed();
  }

  /**
   * Groups including the given one directly, from every file, each with the path of the groups including it
   * in turn ('Online sales › E-shop'), following the first one found at each level.
   */
  parentsOf = (group: AppsGroups): (AppsGroupRef & { path: string[] })[] => {
    let parentsOfRef = (ref: AppsGroupRef): AppsGroupRef[] => {
      let parents = [];
      for (let file of [this.file].concat(Array.from(this.otherFiles.keys()))) {
        for (let candidate of this.groupsOf(file)) {
          if ((candidate.included_apps_groups ?? [])
            .some((included) => included.name == ref.name && (included.file ?? file) == ref.file)) {
            parents.push({name: candidate.name, file: file});
          }
        }
      }
      return parents;
    };

    return parentsOfRef({name: group.name, file: this.file}).map((parent) => {
      let path = [];
      let seen = new Set([`${parent.file}/${parent.name}`]);
      let current = parent;
      while (true) {
        let up = parentsOfRef(current)[0];
        if (up == undefined || seen.has(`${up.file}/${up.name}`)) {
          break;
        }
        seen.add(`${up.file}/${up.name}`);
        path.unshift(up.name);
        current = up;
      }
      return {...parent, path: path};
    });
  }

  /**
   * Groups of the file in the order of their nesting: a group comes right after the group of this file
   * including it, one level deeper. A group included twice is listed once, under the first one.
   */
  nestedGroups = (): { group: AppsGroups, depth: number }[] => {
    let childrenOf = (group: AppsGroups): AppsGroups[] => {
      return (group.included_apps_groups ?? [])
        .filter((included) => included.file == undefined || included.file == this.file)
        .map((included) => this.groups.find((candidate) => candidate.name == included.name))
        .filter((child) => child != undefined);
    };
    let included = new Set(this.groups.flatMap((group) => childrenOf(group)));

    let ordered: { group: AppsGroups, depth: number }[] = [];
    let walk = (group: AppsGroups, depth: number) => {
      if (ordered.some((entry) => entry.group === group)) {
        return;
      }
      ordered.push({group: group, depth: depth});
      childrenOf(group).forEach((child) => walk(child, depth + 1));
    };

    this.groups.filter((group) => !included.has(group)).forEach((group) => walk(group, 0));
    // Groups only reachable through a cycle
    this.groups.forEach((group) => walk(group, 0));
    return ordered;
  }
}
